import {
  Icon123,
  IconChevronDown,
  IconCoins,
  IconUserStar,
  IconLayoutGrid,
  IconSparkles,
  IconStethoscope,
} from "@tabler/icons-react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Burger,
  Center,
  Container,
  Group,
  Menu,
  NumberFormatter,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import classes from "./HeaderMenu.module.css";
import { useTranslation } from "react-i18next";
import UserAvatar from "../../Boilerplate/_Shared/UserAvatar";
import LangSelector from "../../Boilerplate/Auth/LangSelector";
import ColorSchemeToggle from "./components/ColorSchemeToggle";
import { useStore } from "./store";
import { Link, useLocation } from "wouter";
import { getTenantProfile } from "./api";
import { useEffect } from "react";
import { useAssistantStore } from "./Assistant/useAssistant";

export function HeaderMenu({ opened, toggle }) {
  const { t } = useTranslation("common");

  const anonLinks = [{ link: "/auth/login", label: t("nav.login") }];

  const links = [
    { link: "/home", label: t("nav.home"), icon: IconUserStar },
    {
      link: "/imports",
      label: t("nav.imports"),
      icon: IconUserStar,
      links: [
        { link: "/imports", label: t("nav.importedFiles"), icon: IconUserStar },
        { link: "/vendor/submissions", label: t("nav.vendorSubmissions"), icon: IconLayoutGrid },
        { link: "/diagnose", label: t("nav.diagnostic"), icon: IconStethoscope },
      ],
    },
    {
      link: "/pim/products",
      label: t("nav.products"),
      icon: IconUserStar,
      links: [
        { link: "/pim/products", label: t("nav.list"), icon: IconUserStar },
        { link: "/pim/products/grid", label: t("nav.grid"), icon: IconUserStar },
        { link: "/pim/catalog", label: t("nav.catalog"), icon: IconLayoutGrid },
      ],
    },
    { link: "/pim/data-quality", label: t("nav.dataQuality"), icon: Icon123 },
    {
      link: "/pim/schemas",
      label: t("nav.schemas"),
      icon: Icon123,
      links: [
        { link: "/pim/schemas", label: t("nav.schemaList"), icon: IconLayoutGrid },
        { link: "/pim/channels", label: t("nav.channels"), icon: Icon123 },
      ],
    },
    { link: "/translations", label: t("nav.translations"), icon: Icon123 },
  ];

  const tenantAdminLinks = [{ link: "/users", label: t("nav.users"), icon: Icon123 }];

  const globalAdminLinks = [
    { link: "/tenants", label: t("nav.tenants"), icon: Icon123 },
    { link: "/admin/jobs", label: t("nav.jobs"), icon: Icon123 },
    { link: "/esska/products", label: t("nav.productsEsska"), icon: Icon123 },
    { link: "/admin/users", label: t("nav.users"), icon: Icon123 },
  ];
  const [location, setLocation] = useLocation();
  const profile = useStore((state) => state.profile);
  const tenantProfile = useStore((state) => state.tenantProfile);
  const setTenantProfile = useStore((state) => state.setTenantProfile);
  const authToken = useStore((state) => state.authToken);
  // const currentTheme = useComputedColorScheme();

  useEffect(() => {
    if (authToken) {
      getTenantProfile().then((data) => {
        setTenantProfile(data);
      });
    }
  }, [authToken]);

  // console.log("TENANT PROFILE", tenantProfile);
  // console.log("PROFILE", profile);

  let activeLinks = profile ? links : anonLinks;
  if (profile?.role === "TenantAdmin" || profile?.role === "TenantOwner") {
    activeLinks = [...links, ...tenantAdminLinks];
  }
  if (profile?.role === "GlobalAdmin") {
    activeLinks = [...links, ...globalAdminLinks];
  }
  if (tenantProfile?.name?.includes("Esska")) {
    activeLinks = [
      ...activeLinks,
      { link: "/esska/products", label: t("nav.importedProducts"), icon: Icon123 },
    ];
  }

  const items = activeLinks.map((link) => {
    const menuItems = link.links?.map((item) => (
      <Menu.Item
        component={Link}
        to={item.link}
        icon={item.icon}
        key={item.link}
      >
        {item.label}
      </Menu.Item>
    ));

    if (menuItems) {
      return (
        <Menu
          key={link.label}
          trigger="hover"
          transitionProps={{ exitDuration: 0 }}
          withinPortal
        >
          <Menu.Target>
            <Link
              to={link.link}
              className={classes.link}
              onClick={(event) => event.preventDefault()}
            >
              <Center>
                <span className={classes.linkLabel}>{link.label}</span>
                <IconChevronDown size={14} stroke={1.5} />
              </Center>
            </Link>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <Anchor
        component={Link}
        key={link.label}
        to={link.link}
        mod={{ active: location.startsWith(link.link) }}
        className={classes.link}
        // className={location.startsWith(link.link) ? classes.activeLink : classes.link}
        // onClick={(event) => event.preventDefault()}
      >
        {link.label}
      </Anchor>
    );
  });

  return (
    <header className={classes.header}>
      <Container size="xl">
        <div className={classes.inner}>
          {/* Left zone: burger (below lg) + wordmark + primary nav. Kept in one
              Group so space-between only separates this block from the account
              cluster on the right (instead of spreading every child apart). */}
          <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
            <Burger opened={opened} onClick={toggle} size="sm" hiddenFrom="lg" />
            <Group visibleFrom="xs" gap="xs">
              FacetFlux
              {/* <FacetFluxLogoOnly width={48} />
              <FacetFluxFont
                color={currentTheme == "light" ? null : "#FAFAFA"}
                width={100}
              /> */}
            </Group>
            {/* The 8-item nav only fits ~1200px+ (German labels are wider), so it
                shows from lg and collapses into the burger/drawer below that. */}
            <Group gap={4} wrap="nowrap" visibleFrom="lg">
              {items}
            </Group>
          </Group>

          {/* Right zone: assistant + account controls, always grouped together. */}
          <Group gap="sm" wrap="nowrap">
            {profile && (
              <Tooltip label={t("assistant.title")} position="bottom">
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  onClick={useAssistantStore.getState().togglePanel}
                >
                  <IconSparkles size={20} />
                </ActionIcon>
              </Tooltip>
            )}
            {profile ? (
              <Group
                gap="sm"
                wrap="nowrap"
                style={{ cursor: "pointer" }}
                onClick={(event) => {
                  event.preventDefault();
                  setLocation("/profile");
                  // toggle();
                }}
              >
                <Stack gap={4} visibleFrom="sm">
                  <Text>{profile.firstName ?? profile.loginName}</Text>
                  {tenantProfile?.creditTotals?.length > 0 && (
                    <Badge
                      radius="sm"
                      leftSection={<IconCoins size={14} />}
                      size="xs"
                    >
                      <NumberFormatter
                        thousandSeparator
                        value={tenantProfile.creditTotals[0].creditsLeft}
                      />
                    </Badge>
                  )}
                </Stack>
                <div onClick={(e) => e.stopPropagation()}>
                  <ColorSchemeToggle />
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <LangSelector />
                </div>
                <UserAvatar profile={profile} />
              </Group>
            ) : (
              <Group gap="xs">
                <ColorSchemeToggle />
                <LangSelector />
              </Group>
            )}
          </Group>
        </div>
      </Container>
    </header>
  );
}
